import { formatKpiCurrency } from "@/lib/formats/currency";

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return formatKpiCurrency(value);
}

export interface ForecastResult {
  onTrack: boolean;
  delta: number;
  label: string;
}

/**
 * Forecast against annual goal.
 * - Calendar YTD (no trip return-date filter): linear extrapolation of paid commission
 *   to year-end, plus outstanding as pipeline.
 * - Custom return-date slice: compare (paid + outstanding) to the annual goal as-is
 *   with no annualization (KPIs are not a full-year window).
 */
export function computeForecast(
  paidYtd: number,
  outstandingYtd: number,
  goalAmount: number,
  extrapolateToYearEnd: boolean,
): ForecastResult {
  if (goalAmount <= 0) {
    return { onTrack: true, delta: 0, label: "" };
  }

  const combinedPeriod = paidYtd + outstandingYtd;

  if (!extrapolateToYearEnd) {
    const delta = combinedPeriod - goalAmount;
    const onTrack = delta >= 0;
    const absDelta = Math.abs(delta);
    const label = onTrack
      ? `Ahead of annual goal by ${formatCompact(absDelta)} (period total, not annualized)`
      : `${formatCompact(absDelta)} below annual goal (period total, not annualized)`;
    return { onTrack, delta, label };
  }

  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear() + 1, 0, 1);
  const totalDays = (yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24);
  const elapsedDays = Math.max(1, (now.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
  const fractionElapsed = elapsedDays / totalDays;

  const projected = paidYtd / fractionElapsed + outstandingYtd;
  const delta = projected - goalAmount;
  const onTrack = delta >= 0;

  const absDelta = Math.abs(delta);
  const label = onTrack
    ? `On pace to exceed goal by ${formatCompact(absDelta)}`
    : `Projected to finish ${formatCompact(absDelta)} below goal`;

  return { onTrack, delta, label };
}
